import React, { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import LoadingSpinner from "./LoadingSpinner";
import { SEASONAL_SKINS } from "./SeasonalSkins";
import "../index.css";

const API_BASE = process.env.REACT_APP_API_URL;

const HALLOWEEN_THEME = true;

const normalizeItemType = (t) => {
  const s = String(t ?? "").trim().toLowerCase();

  if (
    s === "cardpieces" ||
    s === "cardpiece" ||
    s === "card_pieces" ||
    s === "card-pieces"
  ) {
    return "card_pieces";
  }

  if (
    s === "seasonalaccessory" ||
    s === "seasonal_accessory" ||
    s === "seasonal-accessory" ||
    s === "seasonal accessory" ||
    s === "seasonal_accessories" ||
    s === "seasonal-accessories"
  ) {
    return "seasonal_accessory";
  }

  return s;
};

const CHEST_META = {
  common: { label: "Common Chest", image: "/chest1.png" },
  rare: { label: "Rare Chest", image: "/chest2.png" },
  epic: { label: "Epic Chest", image: "/chest3.png" },
  legendary: { label: "Legendary Chest", image: "/chest4.png" },

  wood: { label: "Dustbound Chest", image: "/shop_chest_5.png" },
  silver: { label: "Cursed Tomb Chest", image: "/shop_chest_3.png" },
  golden: { label: "Lunar Horn Chest", image: "/shop_chest_1.png" },
  gold: { label: "Lunar Horn Chest", image: "/shop_chest_1.png" },
  diamond: { label: "Solar Horizon Chest", image: "/shop_chest_2.png" },
};

const normalizeChestKey = (value) => {
  const s = String(value || "").trim().toLowerCase();

  if (s.includes("diamond")) return "diamond";
  if (s.includes("golden") || s.includes("gold")) return "golden";
  if (s.includes("silver")) return "silver";
  if (s.includes("wood")) return "wood";

  if (s.includes("legendary")) return "legendary";
  if (s.includes("epic")) return "epic";
  if (s.includes("rare")) return "rare";
  if (s.includes("common")) return "common";

  return "";
};

const getChestMeta = (value) => {
  const key = normalizeChestKey(value);
  return CHEST_META[key] || null;
};

const normalizeAssetPath = (value) => {
  const src = String(value || "").trim();

  if (!src) return "";

  if (
    src.startsWith("/") ||
    src.startsWith("http://") ||
    src.startsWith("https://") ||
    src.startsWith("data:")
  ) {
    return src;
  }

  return `/${src}`;
};

const clampQuantity = (value) => {
  const n = Math.floor(Number(value));
  if (!Number.isFinite(n)) return 1;
  return Math.max(1, Math.min(10, n));
};

const safeArray = (value) => (Array.isArray(value) ? value : []);

const toBool = (value) => {
  if (value === true) return true;
  if (value === false) return false;
  if (value === 1) return true;
  if (value === 0) return false;

  const s = String(value ?? "").trim().toLowerCase();

  if (s === "true" || s === "1" || s === "yes") return true;
  if (s === "false" || s === "0" || s === "no" || s === "") return false;

  return false;
};

const firstDefined = (...values) =>
  values.find((v) => v !== undefined && v !== null);

const formatCompactNumber = (value) => {
  const n = Number(value || 0);
  if (!Number.isFinite(n)) return "0";
  return n.toLocaleString("en-US");
};

const normalizeCurrencyPacks = (raw) => {
  const currency = raw?.currency || {};

  const list =
    raw?.currencyPacks ||
    raw?.currency_packs ||
    raw?.eventCurrencyPacks ||
    raw?.event_currency_packs ||
    currency.packs ||
    currency.skus ||
    currency.offers ||
    currency.bundles ||
    [];

  if (!Array.isArray(list)) return [];

  return list
    .map((pack, idx) => {
      const amount = Number(
        firstDefined(
          pack?.amount,
          pack?.currencyAmount,
          pack?.currency_amount,
          pack?.quantity,
          pack?.qty
        )
      );

      const price = Number(
        firstDefined(
          pack?.starsPrice,
          pack?.stars_price,
          pack?.priceStars,
          pack?.price_stars,
          pack?.telegramStars,
          pack?.telegram_stars,
          pack?.price,
          pack?.stars
        )
      );

      const discountStars = Number(
        firstDefined(
          pack?.saleStarsPrice,
          pack?.sale_stars_price,
          pack?.discountStars,
          pack?.discount_stars,
          pack?.saleStars,
          pack?.sale_stars,
          0
        )
      );

      return {
        ...pack,
        id: String(pack?.id || pack?.key || `event-currency-${idx}`),
        amount,
        price,
        discountStars: Number.isFinite(discountStars) ? discountStars : 0,
        enabled: pack?.enabled !== false,
      };
    })
    .filter(
      (pack) =>
        pack.enabled !== false &&
        Number.isFinite(pack.amount) &&
        pack.amount > 0 &&
        Number.isFinite(pack.price) &&
        pack.price > 0
    );
};

const formatEndsIn = (endsAt) => {
  if (!endsAt) return "";

  const target = new Date(endsAt).getTime();
  if (!Number.isFinite(target)) return "";

  const ms = target - Date.now();
  if (ms <= 0) return "Ended";

  const totalMinutes = Math.floor(ms / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;

  if (days > 0) return `${days}D ${hours}H ${minutes}Min`;
  if (hours > 0) return `${hours}H ${minutes}Min`;
  return `${minutes}Min`;
};

const normalizeMission = (mission, rewardIcon) => ({
  ...mission,
  id: String(mission?.id || mission?.key || mission?.title || mission?.label || Math.random()),
  title: mission?.title || mission?.label || mission?.name || "Mission",
  reward: Number(firstDefined(mission?.reward, mission?.rewardAmount, mission?.currencyReward, 0)),
  rewardLabel: mission?.rewardLabel || mission?.reward_label || "",
  rewardIcon: mission?.rewardIcon || mission?.reward_icon || rewardIcon || "",
  progress: Number(firstDefined(mission?.progress, mission?.current, mission?.count, 0)),
  target: Number(firstDefined(mission?.target, mission?.goal, mission?.required, 1)),

  completed:
    toBool(mission?.completed) ||
    toBool(mission?.claimed) ||
    toBool(mission?.isCompleted),

  claimable:
    toBool(mission?.claimable) ||
    toBool(mission?.canClaim),

  locked: toBool(mission?.locked),
  opensIn: mission?.opensIn || mission?.opens_in || mission?.unlockText || "",
});

const normalizeShopItem = (row) => {
 const base =
  row?.fullItem ||
  row?.full_item ||
  row?.item ||
  row?.shopItem ||
  row?.shop_item ||
  row?.rewardItem ||
  row?.reward_item ||
  row;

  const type = String(
    base?.type ||
      row?.type ||
      row?.item_type ||
      row?.itemType ||
      row?.reward_type ||
      row?.rewardType ||
      ""
  ).trim();

  

  return {
    ...base,
    ...row,

    _raw: row,
    _base: base,

    id: String(
      row?.id ||
        row?.itemId ||
        row?.item_id ||
        row?.shopItemId ||
        row?.shop_item_id ||
        base?.id ||
        base?.itemId ||
        base?.item_id ||
        base?.name ||
        Math.random()
    ),

    name:
      base?.name ||
      base?.display_name ||
      base?.displayName ||
      row?.name ||
      row?.title ||
      row?.display_name ||
      row?.displayName ||
      "Item",

    type,

    chestType:
      base?.chestType ||
      base?.chest_type ||
      row?.chestType ||
      row?.chest_type ||
      "",

    _icon:
      base?._icon ||
      row?._icon ||
      "",

    name_category:
      base?.name_category ||
      base?.nameCategory ||
      row?.name_category ||
      row?.nameCategory ||
      "",

    name_value:
      base?.name_value ||
      base?.nameValue ||
      row?.name_value ||
      row?.nameValue ||
      "",

      season_name:
  row?.season_name ||
  row?.seasonName ||
  base?.season_name ||
  base?.seasonName ||
  row?.fullItem?.season_name ||
  row?.fullItem?.seasonName ||
  "",

accessory_total_items: Number(
  base?.accessory_total_items ??
  base?.accessoryTotalItems ??
  row?.accessory_total_items ??
  row?.accessoryTotalItems ??
  row?.fullItem?.accessory_total_items ??
  row?.fullItem?.accessoryTotalItems ??
  0
),

    price: Number(
      row?.price ??
        row?.cost ??
        row?.price_event ??
        row?.priceEvent ??
        row?.price_runes ??
        row?.price_sparks ??
        base?.price ??
        base?.cost ??
        base?.price_event ??
        base?.priceEvent ??
        base?.price_runes ??
        base?.price_sparks ??
        0
    ),

    currency:
      row?.currency ||
      row?.priceCurrency ||
      row?.price_currency ||
      base?.currency ||
      base?.priceCurrency ||
      base?.price_currency ||
      "event",

    stockCurrent:
      row?.stockCurrent ??
      row?.stock_current ??
      row?.owned ??
      row?.limit_used ??
      base?.stockCurrent ??
      base?.stock_current ??
      base?.owned ??
      base?.limit_used ??
      null,

    stockMax:
      row?.stockMax ??
      row?.stock_max ??
      row?.limit ??
      row?.buyLimit ??
      base?.stockMax ??
      base?.stock_max ??
      base?.limit ??
      base?.buyLimit ??
      null,

    soldOut: !!(
      row?.soldOut ||
      row?.sold_out ||
      base?.soldOut ||
      base?.sold_out
    ),
  };
};

const normalizeEvent = (raw) => {
  if (!raw || typeof raw !== "object") return null;

  const currency = raw.currency || {};
  const currencyIcon =
    raw.currencyIcon ||
    raw.currency_icon ||
    currency.iconUrl ||
    currency.icon_url ||
    "";

  const daily = safeArray(
    raw?.missions?.daily ||
      raw?.dailyMissions ||
      raw?.daily_missions
  ).map((mission) => normalizeMission(mission, currencyIcon));

  const seasonal = safeArray(
    raw?.missions?.seasonal ||
      raw?.seasonalMissions ||
      raw?.seasonal_missions
  ).map((mission) => normalizeMission(mission, currencyIcon));

  const special = safeArray(
    raw?.shop?.special ||
      raw?.specialItems ||
      raw?.special_items ||
      raw?.eventSpecialItems
  ).map(normalizeShopItem);

  const items = safeArray(
    raw?.shop?.items ||
      raw?.eventItems ||
      raw?.event_items
  ).map(normalizeShopItem);

  return {
    ...raw,

    id: raw.id || raw.key || raw.eventKey || "",
    name: raw.name || raw.title || "",
    subtitle: raw.subtitle || raw.endsLabel || "Ends in",
    endsAt: raw.endsAt || raw.ends_at || null,
    bannerImage: raw.bannerImage || raw.banner_image || raw.image || "",

    currencyKey:
      raw.currencyKey ||
      raw.currency_key ||
      currency.key ||
      "",

    currencyName:
      raw.currencyName ||
      raw.currency_name ||
      currency.pluralName ||
      currency.name ||
      "",

    currencyIcon,

    balance: Number(firstDefined(raw.balance, raw.currencyBalance, 0)),
    currencyPacks: normalizeCurrencyPacks(raw),

    tabs: {
      home: raw?.tabs?.home || "HOME",
      shop: raw?.tabs?.shop || "SHOP",
    },

    missions: {
      daily,
      seasonal,
    },

    shop: {
      special,
      items,
    },

    chest: raw.chest || null,
  };
};

function EventCountdown({ endsAt }) {
  const [text, setText] = useState(() => formatEndsIn(endsAt));

  useEffect(() => {
    setText(formatEndsIn(endsAt));

    const timer = setInterval(() => {
      setText(formatEndsIn(endsAt));
    }, 60000);

    return () => clearInterval(timer);
  }, [endsAt]);

  return <>{text || "—"}</>;
}

function EventCountdownBox({ endsAt }) {
  return (
    <div
      style={{
        width: 150,
height: 54,
        margin: "0 auto",
        borderRadius: 10,
        background: "rgba(10, 3, 1, 0.5)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 0,
        padding: "4px 6px",
        boxSizing: "border-box",
      }}
    >
      <img
        src="/halloweenclock.png"
        alt=""
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
        style={{
          width: 42,
          height: 42,
          objectFit: "contain",
          flexShrink: 0,
          filter: "drop-shadow(0 2px 2px rgba(0,0,0,.75))",
        }}
      />

      <div
        style={{
          flex: 1,
          minWidth: 0,
          textAlign: "center",
        }}
      >
        <div
          style={{
            color: "#ffca5c",
            fontSize: 13,
            lineHeight: "21px",
            fontWeight: 600,
            textShadow: "2px 3px 0 #000",
            whiteSpace: "nowrap",
          }}
        >
          Event Ends In
        </div>

        <div
          style={{
            color: "#fff",
            fontSize: 13,
            lineHeight: "19px",
            fontWeight: 600,
            whiteSpace: "nowrap",
          }}
        >
          <EventCountdown endsAt={endsAt} />
        </div>
      </div>
    </div>
  );
}

function EventCurrencyHeaderButton({
  active,
  balance,
  currencyName,
  currencyIcon,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        width: "100%",
height: 46,
minWidth: 0,
        padding: "5px 0px",
        margin: 0,
        boxSizing: "border-box",

        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 1,

       background: active
  ? "rgba(112, 61, 27, 0.96)"
  : "rgba(95, 49, 18, 0.76)",

border: active
  ? "2px solid rgba(135, 78, 36, 0.98)"
  : "2px solid rgba(70, 32, 10, 0.95)",

borderRadius: 7,

boxShadow: active
  ? "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.24), 0 2px 0 rgba(0,0,0,0.24)"
  : "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.18), 0 2px 0 rgba(0,0,0,0.18)",
  
             borderRadius: 8,

        color: "#fff",
        cursor: "pointer",
        appearance: "none",
        WebkitAppearance: "none",
      }}
    >
      {currencyIcon ? (
        <img
          src={currencyIcon}
          alt=""
          onError={(e) => {
            e.currentTarget.style.display = "none";
          }}
          style={{
            width: 24,
            height: 24,
            objectFit: "contain",
            flexShrink: 0,
            filter: "drop-shadow(0 2px 2px rgba(0,0,0,.75))",
          }}
        />
      ) : null}

      <div
        style={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          textAlign: "center",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 3,
            color: "#ffd35c",
            fontSize: 12,
            lineHeight: "18px",
            fontWeight: 600,
            whiteSpace: "nowrap",
            textShadow:
              "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
          }}
        >
          {formatCompactNumber(balance)}

          <span
            style={{
              width: 16,
              height: 16,
              borderRadius: "50%",
              background: "#ffcc32",
              color: "#2b1600",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 18,
              fontWeight: 600,
              lineHeight: "16px",
              textShadow: "none",
              boxShadow: "0 2px 0 rgba(0,0,0,.45)",
            }}
          >
            +
          </span>
        </div>

        <div
          style={{
            width: "100%",
            marginTop: 2,
            color: "#fff",
            fontSize: 8,
            fontFamily: '"Roboto", sans-serif',
            lineHeight: "14px",
            fontWeight: 600,
            textTransform: "uppercase",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            textShadow: "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000",
          }}
        >
          {currencyName || "Event Currency"}
        </div>
      </div>
    </button>
  );
}

function EventsPage({
  user,
  setUser,
  onNavigate,
  eventKey = "",
  initialView = "home",
}) {
  const normalizeView = (value) => {
  const v = String(value || "").toLowerCase();

  if (v === "shop") return "shop";
  if (v === "currency" || v === "buycurrency" || v === "event_currency") {
    return "currency";
  }

  return "home";
};

const [view, setView] = useState(() => normalizeView(initialView));

  const [eventPayload, setEventPayload] = useState(null);
  const [loading, setLoading] = useState(true);

  const [selectedItem, setSelectedItem] = useState(null);
  const [quantity, setQuantity] = useState(1);
  const [showConfirm, setShowConfirm] = useState(false);

  const [selectedCurrencyPackIdx, setSelectedCurrencyPackIdx] = useState(null);
const [buyingCurrency, setBuyingCurrency] = useState(false);

const [confirmCurrencyOpen, setConfirmCurrencyOpen] = useState(false);
const [currencyToBuy, setCurrencyToBuy] = useState(null);

  const [infoItem, setInfoItem] = useState(null);

  const [buying, setBuying] = useState(false);
  const [claimingMissionId, setClaimingMissionId] = useState(null);
  
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [successReward, setSuccessReward] = useState(null);

  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const event = useMemo(() => normalizeEvent(eventPayload), [eventPayload]);

  useEffect(() => {
  setView(normalizeView(initialView));
}, [initialView]);

  const loadCurrentEvent = async () => {
    const token = localStorage.getItem("token");

    if (!token || !API_BASE) {
      setLoading(false);
      setErrorMessage("Missing API URL or token.");
      setShowError(true);
      return;
    }

    try {
      setLoading(true);

      const url = eventKey
        ? `${API_BASE}/api/events/current?eventKey=${encodeURIComponent(eventKey)}`
        : `${API_BASE}/api/events/current`;

      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
  setErrorMessage(data?.error || "Could not load event");
  setShowError(true);
  return;
}

      const loadedEvent = data.event || data.currentEvent || null;

     if (!loadedEvent) {
  setErrorMessage("No event returned from backend.");
  setShowError(true);
  return;
}

      setEventPayload(loadedEvent);
    } catch (err) {
  console.warn("Could not load current event:", err);
  setErrorMessage("Network error while loading event");
  setShowError(true);
} finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const token = localStorage.getItem("token");

      if (!token || !API_BASE) {
        if (!cancelled) {
          setLoading(false);
          setErrorMessage("Missing API URL or token.");
          setShowError(true);
        }
        return;
      }

      try {
        setLoading(true);

        const url = eventKey
          ? `${API_BASE}/api/events/current?eventKey=${encodeURIComponent(eventKey)}`
          : `${API_BASE}/api/events/current`;

        const res = await fetch(url, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await res.json().catch(() => ({}));

        if (cancelled) return;

       if (!res.ok) {
  setErrorMessage(data?.error || "Could not load event");
  setShowError(true);
  return;
}

        const loadedEvent = data.event || data.currentEvent || null;

       if (!loadedEvent) {
  setEventPayload(null);
  setErrorMessage("No event returned from backend.");
  setShowError(true);
  return;
}

        setEventPayload(loadedEvent);
     } catch (err) {
  if (!cancelled) {
    console.warn("Could not load current event:", err);
    setErrorMessage("Network error while loading event");
    setShowError(true);
  }
} finally {
        if (!cancelled) setLoading(false);
      }
    };

    run();

    return () => {
      cancelled = true;
    };
  }, [eventKey]);

  const eventBalance = Number(event?.balance || 0);

  const currentEndsAt = event?.endsAt || event?.ends_at;

  const updateUserFromResponse = (data) => {
    if (!data?.user) return;

    setUser?.((prev) => ({
      ...prev,
      ...data.user,
    }));
  };

const markMissionClaimedInPayload = (payload, claimedMission) => {
  if (!payload) return payload;

  const claimedId = String(claimedMission?.id || "");

  const markMission = (m) => {
    const currentId = String(m?.id || m?.key || m?.title || m?.label || "");

    if (currentId !== claimedId) return m;

    const finalTarget = Number(
      m?.target ||
        m?.goal ||
        m?.required ||
        claimedMission?.target ||
        1
    );

    return {
      ...m,
      completed: true,
      claimed: true,
      isCompleted: true,
      claimable: false,
      canClaim: false,
      progress: finalTarget,
      current: finalTarget,
      count: finalTarget,
    };
  };

  return {
    ...payload,

    missions: {
      ...payload.missions,
      daily: Array.isArray(payload?.missions?.daily)
        ? payload.missions.daily.map(markMission)
        : payload?.missions?.daily,
      seasonal: Array.isArray(payload?.missions?.seasonal)
        ? payload.missions.seasonal.map(markMission)
        : payload?.missions?.seasonal,
    },

    dailyMissions: Array.isArray(payload?.dailyMissions)
      ? payload.dailyMissions.map(markMission)
      : payload?.dailyMissions,

    daily_missions: Array.isArray(payload?.daily_missions)
      ? payload.daily_missions.map(markMission)
      : payload?.daily_missions,

    seasonalMissions: Array.isArray(payload?.seasonalMissions)
      ? payload.seasonalMissions.map(markMission)
      : payload?.seasonalMissions,

    seasonal_missions: Array.isArray(payload?.seasonal_missions)
      ? payload.seasonal_missions.map(markMission)
      : payload?.seasonal_missions,
  };
};

  const claimMission = async (mission) => {
    if (!event?.id) return;
    const progress = Number(mission.progress || 0);
const target = Number(mission.target || 1);
const reallyCompleted = mission.completed === true && progress >= target;

if (!mission?.id || mission.locked || reallyCompleted) return;

    if (claimingMissionId) return;

    
    const canClaim = mission.claimable || progress >= target;

    if (!canClaim) return;

    try {
      setClaimingMissionId(mission.id);

      const res = await fetch(`${API_BASE}/api/events/missions/claim`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          eventKey: event.id,
          missionId: mission.id,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setErrorMessage(data?.error || "Could not claim mission reward");
        setShowError(true);
        return;
      }

 updateUserFromResponse(data);

setEventPayload((prev) =>
  markMissionClaimedInPayload(data.event || data.currentEvent || prev, mission)
);

setSuccessReward({
  amount: Number(data?.claimedAmount ?? data?.rewardAmount ?? mission.reward ?? 0),
  label: mission.rewardLabel || event?.currencyName || "",
  icon: event?.currencyIcon || mission.rewardIcon || "",
});

setSuccessMessage("Mission Reward Claimed!");
setShowSuccess(true);
    } catch (err) {
      setErrorMessage("Network error");
      setShowError(true);
    } finally {
      setClaimingMissionId(null);
    }
  };

          const isTMA = typeof window !== "undefined" && !!window.Telegram?.WebApp;

const purchaseEventCurrencyStars = async () => {
  if (!event?.id || buyingCurrency) return;

  const sku = event?.currencyPacks?.[selectedCurrencyPackIdx];
  if (!sku) return;

  if (!isTMA || !window.Telegram.WebApp.openInvoice) {
    setErrorMessage(
      `${event?.currencyName || "Event currency"} can only be purchased with Telegram Stars inside the Telegram app.`
    );
    setShowError(true);
    return;
  }

  try {
    setBuyingCurrency(true);

    const res = await fetch(`${API_BASE}/api/events/stars/create-order`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        eventKey: event.id,
        amount: sku.amount,
      }),
    });

    const data = await res.json().catch(() => ({}));

    if (!res.ok || !data.ok) {
      setErrorMessage(data?.error || "Could not start Telegram Stars payment");
      setShowError(true);
      setBuyingCurrency(false);
      return;
    }

    const { orderId, invoiceLink } = data;

    window.Telegram.WebApp.openInvoice(invoiceLink, async (status) => {
      if (status !== "paid") {
        setErrorMessage(
          status === "cancelled" ? "Payment cancelled" : "Payment failed"
        );
        setShowError(true);
        setBuyingCurrency(false);
        return;
      }

      let tries = 0;
      const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

      while (tries < 20) {
        const chk = await fetch(`${API_BASE}/api/telegram/stars/order/${orderId}`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });

        if (chk.ok) {
          const j = await chk.json().catch(() => ({}));
          if (j.status === "paid") break;
        }

        tries += 1;
        await wait(1500);
      }

      await loadCurrentEvent();

      setSuccessReward({
        amount: Number(sku.amount || 0),
        label: event?.currencyName || "Event Currency",
        icon: event?.currencyIcon || "/ankh.png",
      });

      setSuccessMessage(`${event?.currencyName || "Event currency"} Purchased!`);
      setShowSuccess(true);
      setSelectedCurrencyPackIdx(null);
      setBuyingCurrency(false);
    });
  } catch (err) {
    console.error("Event currency Stars payment error:", err);
    setErrorMessage("Network error");
    setShowError(true);
    setBuyingCurrency(false);
  }
};

  const openPurchase = (item) => {
    if (item?.soldOut) return;

    setSelectedItem(item);
    setQuantity(1);
    setShowConfirm(true);
  };

  const purchaseSelectedItem = async () => {
    if (!event?.id) return;
    if (!selectedItem?.id || buying) return;

    try {
      setBuying(true);

      const res = await fetch(`${API_BASE}/api/events/shop/purchase`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          eventKey: event.id,
          itemId: selectedItem.id,
          quantity,
        }),
      });

      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        setShowConfirm(false);
        setErrorMessage(data?.error || "Purchase failed");
        setShowError(true);
        return;
      }

      updateUserFromResponse(data);

      if (data.event) {
        setEventPayload(data.event);
      } else {
        await loadCurrentEvent();
      }

     setShowConfirm(false);

setSuccessReward({
  amount: quantity,
  label: selectedItem?.name || "Item",
  icon: getItemIcon(selectedItem),
});

setSuccessMessage("Item purchased!");
setShowSuccess(true);
    } catch (err) {
      setShowConfirm(false);
      setErrorMessage("Network error");
      setShowError(true);
    } finally {
      setBuying(false);
    }
  };

const getItemIcon = (it) => {
  const base = it?._base || it?.item || it?.shopItem || it?.shop_item || it;

  const t = normalizeItemType(
    base?.type ||
      it?.type ||
      it?.fullItem?.type ||
      it?._raw?.type ||
      ""
  );

  const nameCategory = String(
    base?.name_category ||
      base?.nameCategory ||
      it?.name_category ||
      it?.nameCategory ||
      it?.fullItem?.name_category ||
      it?.fullItem?.nameCategory ||
      ""
  ).toLowerCase();

  const nameValue = String(
    base?.name_value ||
      base?.nameValue ||
      it?.name_value ||
      it?.nameValue ||
      it?.fullItem?.name_value ||
      it?.fullItem?.nameValue ||
      ""
  ).toLowerCase();

  const text = String(
    [
      base?.name,
      base?.display_name,
      base?.displayName,
      base?.title,
      it?.name,
      it?.display_name,
      it?.displayName,
      it?.title,
      it?.fullItem?.name,
      it?.fullItem?.display_name,
      it?.fullItem?.displayName,
      it?.fullItem?.title,
      base?.type,
      it?.type,
      it?.fullItem?.type,
      nameCategory,
      nameValue,
    ]
      .filter(Boolean)
      .join(" ")
  ).toLowerCase();

const explicitIcon = normalizeAssetPath(
  it?.image_url ||
  it?.imageUrl ||
  it?.item?.image_url ||
  it?.item?.imageUrl ||
  it?.fullItem?.image_url ||
  it?.fullItem?.imageUrl ||
  it?._base?.image_url ||
  it?._base?.imageUrl ||
  it?._raw?.image_url ||
  it?._raw?.imageUrl ||
  it?.iconUrl ||
  it?.icon_url ||
  it?._icon ||
  it?.icon ||
  it?.image ||
  ""
);

          if (t === "card") {
  const rarity = String(
    base?.card_rarity ||
    base?.cardRarity ||
    base?.rarity ||
    it?.card_rarity ||
    it?.cardRarity ||
    it?.rarity ||
    it?.fullItem?.card_rarity ||
    it?.fullItem?.cardRarity ||
    it?.fullItem?.rarity ||
    it?._raw?.card_rarity ||
    it?._raw?.cardRarity ||
    it?._raw?.rarity ||
    text
  ).toLowerCase();

  if (rarity.includes("legendary")) return "/legendary.png";
  if (rarity.includes("epic")) return "/epic.png";
  if (rarity.includes("rare")) return "/rare.png";
  if (rarity.includes("common")) return "/common.png";

  return "/common.png";
}

  if (explicitIcon && explicitIcon !== "/pack.png") {
    return explicitIcon;
  }

  if (text.includes("anubis") && text.includes("chest")) {
    return "/anubis_chest.png";
  }

  if (text.includes("rune")) return "/runes.png";
  if (text.includes("spark")) return "/sparks.png";
  if (text.includes("spin")) return "/fortuneicon.png";

  if (
    text.includes("card piece") ||
    text.includes("card_piece") ||
    text.includes("pieces")
  ) {
    return "/pieces.png";
  }

  if (text.includes("booster") || text.includes("energy")) return "/energy.png";
  if (text.includes("ability")) return "/fightlog.png";
  if (text.includes("shield")) return "/shield.png";
  if (text.includes("gamepass") || text.includes("pass")) return "/passicon.png";
  if (text.includes("tapi")) return "/icon_tapi.png";
  if (text.includes("evolve")) return "/evolveicon.png";

  if (
    t === "chest" ||
    text.includes("chest") ||
    text.includes("wood") ||
    text.includes("silver") ||
    text.includes("gold") ||
    text.includes("diamond")
  ) {
    const chestType =
      base?.chestType ||
      base?.chest_type ||
      it?.chestType ||
      it?.chest_type ||
      text;

    const meta = getChestMeta(chestType);
    if (meta?.image) return meta.image;

    return "/shop_chest_3.png";
  }

  switch (t) {
    case "runes":
      return "/runes.png";

    case "sparks":
      return "/sparks.png";

    case "booster":
    case "boosters":
    case "energy":
      return "/energy.png";

    case "tapi":
      return "/icon_tapi.png";

    case "fragment":
    case "fragments":
    case "seasonal_accessory":
    case "skin":
    case "skins":
      return "/fragment.png";

    case "ability":
    case "abilities":
      return "/fightlog.png";

    case "shield":
    case "trophy_shield":
      return "/shield.png";

    case "card_pieces":
    case "cardpieces":
      return "/pieces.png";

    case "spins":
    case "spin":
      return "/fortuneicon.png";

    case "evolve":
      return "/evolveicon.png";

    case "name":
      if (nameCategory === "banners" || nameCategory === "banner") {
        return "/banner_icon.png";
      }

      if (
        nameCategory === "emogy" ||
        nameCategory === "emoji" ||
        nameCategory === "emojy"
      ) {
        return "/emogy_icon.png";
      }

      return "/style_icon.png";

    case "card": {
  const rarity = String(
    base?.card_rarity ??
    base?.cardRarity ??
    base?.rarity ??
    it?.card_rarity ??
    it?.cardRarity ??
    it?.rarity ??
    it?.fullItem?.card_rarity ??
    it?.fullItem?.cardRarity ??
    it?.fullItem?.rarity ??
    it?._raw?.card_rarity ??
    it?._raw?.cardRarity ??
    it?._raw?.rarity ??
    ""
  )
    .trim()
    .toLowerCase();

  if (rarity.includes("legendary")) return "/legendary.png";
  if (rarity.includes("epic")) return "/epic.png";
  if (rarity.includes("rare")) return "/rare.png";
  if (rarity.includes("common")) return "/common.png";

  return "/common.png";
}

    default:
      return "/pack.png";
  }
};

  const getPriceIcon = (item) => {
    const currency = String(item?.currency || item?.priceCurrency || "event").toLowerCase();

    if (currency === "sparks") return "/sparks.png";
    if (currency === "runes") return "/runes.png";
    if (currency === "stars" || currency === "telegram_stars") return "/star.png";

    return event?.currencyIcon || "";
  };

  const getPrice = (item) =>
    Number(firstDefined(item?.price, item?.cost, item?.price_event, item?.priceEvent, 0));

         const EventTabButton = ({ active, children, onClick }) => (
  <button
    type="button"
    onClick={onClick}
    style={{
      width: "100%",
      height: 46,
      padding: "0 8px",
      margin: 0,
      boxSizing: "border-box",

     background: active
  ? "rgba(112, 61, 27, 0.96)"
  : "rgba(95, 49, 18, 0.76)",

border: active
  ? "2px solid rgba(135, 78, 36, 0.98)"
  : "2px solid rgba(70, 32, 10, 0.95)",

borderRadius: 7,

boxShadow: active
  ? "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.24), 0 2px 0 rgba(0,0,0,0.24)"
  : "inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -2px 0 rgba(0,0,0,0.18), 0 2px 0 rgba(0,0,0,0.18)",

      color: "#fff",
      fontSize: 16,
      lineHeight: "42px",
      fontWeight: 600,
      textAlign: "center",
        textShadow:
        "-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000, 0 1px 1px rgba(0,0,0,.75)",

      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",

      cursor: "pointer",
      appearance: "none",
      WebkitAppearance: "none",
      opacity: active ? 1 : 0.88,
    }}
  >
    {children}
  </button>
);

const EventSection = ({ title, children, bodyStyle = {} }) => (
  <section
    style={{
      display: "block",
      width: "calc(100% + 36px)",
      marginLeft: -18,
      marginRight: -18,
      marginBottom: 18,
      padding: 0,
      position: "relative",
      clear: "both",
      boxSizing: "border-box",
    }}
  >
    <div
  className="modal_title"
  style={{
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    position: "relative",
    float: "none",
    clear: "both",

    top: "auto",
    right: "auto",
    bottom: "auto",
    left: "auto",
    transform: "none",

    width: "100%",
    height: 48,
    minHeight: 48,

    margin: 0,
    padding: "0 28px",

    boxSizing: "border-box",
    textAlign: "center",
    lineHeight: "normal",
    zIndex: 2,
  }}
>
  {title}
</div>

    <div
      className="modal_body modal_body_2"
      style={{
        display: "block",

        position: "relative",
        float: "none",
        clear: "both",

        top: "auto",
        right: "auto",
        bottom: "auto",
        left: "auto",
        transform: "none",

        width: "calc(100% - 32px)",
        minHeight: 0,
        
        boxSizing: "border-box",
        zIndex: 1,

        ...bodyStyle,
      }}
    >
      {children}
    </div>
  </section>
);

          const getMissionIcon = (mission) => {
  const text = String(
    [
      mission?.type,
      mission?.missionType,
      mission?.mission_type,
      mission?.key,
      mission?.id,
      mission?.title,
      mission?.label,
      mission?.name,
    ]
      .filter(Boolean)
      .join(" ")
  ).toLowerCase();

  if (text.includes("play") && text.includes("battle")) {
    return "/event_1s.png";
  }

  if (text.includes("win") && text.includes("battle")) {
    return "/event_2s.png";
  }

  if (text.includes("complete") && text.includes("task")) {
    return "/event_3s.png";
  }

  if (text.includes("open") && text.includes("chest")) {
    return "/event_4s.png";
  }

  if (text.includes("spin") || text.includes("wheel")) {
    return "/event_5s.png";
  }

  if (text.includes("evolve")) {
    return "/event_6s.png";
  }

  if (text.includes("refer") || text.includes("friend")) {
    return "/event_7s.png";
  }

  if (text.includes("buy") || text.includes("purchase")) {
    return "/event_8s.png";
  }

  if (text.includes("tap")) {
    return "/event_9s.png";
  }

  if (text.includes("merge")) {
    return "/event_10s.png";
  }

  if (text.includes("marketplace") || text.includes("list card") || text.includes("list_card")) {
    return "/event_13s.png";
  }

  if (text.includes("trophy") || text.includes("trophies")) {
    return "/event_11s.png";
  }

  if (text.includes("rune")) {
    return "/event_14s.png";
  }

  if (text.includes("spark")) {
    return "/event_12s.png";
  }

  return "/event_task.png";
};

const MissionCard = ({ mission, compact = false, variant = "daily" }) => {
  const target = Math.max(1, Number(mission.target || 1));
  const progress = Math.max(0, Math.min(target, Number(mission.progress || 0)));
  const percent = Math.round((progress / target) * 100);

  const isReallyCompleted =
  mission.completed === true ||
  mission.claimed === true ||
  mission.isCompleted === true;


  const isClaimable =
    !mission.locked &&
    !isReallyCompleted &&
    (mission.claimable || progress >= target);

  const isClaiming = claimingMissionId === mission.id;

  const isSeasonal = variant === "seasonal";
          const isActiveMission =
  isSeasonal &&
  !mission.locked &&
  !isReallyCompleted;

  const dailyIcon = getMissionIcon(mission);

 if (isSeasonal) {
  const unlockAt =
    mission.unlockAt ||
    mission.unlock_at ||
    mission.availableAt ||
    mission.available_at ||
    mission.startsAt ||
    mission.starts_at;

  const getLockedText = () => {
    if (mission.opensIn || mission.unlockText || mission.unlock_text || mission.opens_in) {
      return (
        mission.opensIn ||
        mission.unlockText ||
        mission.unlock_text ||
        mission.opens_in
      );
    }

    if (!unlockAt) return "SOON";

    const targetTime = new Date(unlockAt).getTime();
    if (!Number.isFinite(targetTime)) return "SOON";

    const ms = targetTime - Date.now();
    if (ms <= 0) return "SOON";

    const totalMinutes = Math.floor(ms / 60000);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) return `${days}D ${hours}H`;
    if (hours > 0) return `${hours}H ${minutes}MIN`;
    return `${minutes}MIN`;
  };

  const lockedText = getLockedText();

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={() => {
        if (isClaimable) claimMission(mission);
      }}
      style={{
        width: "100%",
        height: 72,
        position: "relative",
        boxSizing: "border-box",
        display: "grid",
        gridTemplateColumns: "72px 1fr",
        gap: 7,
        cursor: isClaimable ? "pointer" : "default",
        opacity: mission.locked ? 0.72 : 1,
      }}
    >
      <div
        style={{
          height: 72,
          borderRadius: 8,
          background: mission.locked
  ? "rgba(28,28,28,.92)"
  : isActiveMission
    ? "linear-gradient(180deg, #8b2410 0%, #3b0804 100%)"
    : "rgba(18,3,3,.94)",

border: mission.locked
  ? "2px solid #555"
  : isActiveMission
    ? "2px solid #ff8a2a"
    : "2px solid #a91c12",

boxShadow: isActiveMission
  ? "0 3px 0 rgba(0,0,0,.65), 0 0 10px rgba(255,138,42,.35)"
  : "0 3px 0 rgba(0,0,0,.65)",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          boxSizing: "border-box",
          overflow: "hidden",
        }}
      >
        {event?.currencyIcon ? (
          <img
            src={event.currencyIcon}
            alt=""
            style={{
              width: 30,
              height: 30,
              objectFit: "contain",
              filter: mission.locked
                ? "grayscale(1) drop-shadow(0 2px 2px rgba(0,0,0,.75))"
                : "drop-shadow(0 2px 2px rgba(0,0,0,.75))",
            }}
          />
        ) : null}

        <div
          style={{
            marginTop: 2,
            color: "#fff",
            fontSize: 14,
            lineHeight: "18px",
            fontWeight: 600,
            textShadow: "2px 3px 0 #000",
            whiteSpace: "nowrap",
          }}
        >
          {formatCompactNumber(mission.reward)}
        </div>
      </div>

      <div
        style={{
          height: 72,
          position: "relative",
          borderRadius: 8,
          background: mission.locked
  ? "rgba(45,45,45,.84)"
  : isActiveMission
    ? "linear-gradient(180deg, #a83213 0%, #731207 52%, #420703 100%)"
    : "rgba(65,9,4,.86)",

boxShadow: isActiveMission
  ? "0 3px 0 rgba(0,0,0,.55), inset 0 1px 0 rgba(255,210,120,.25), 0 0 12px rgba(255,98,34,.28)"
  : "0 3px 0 rgba(0,0,0,.55)",

border: isActiveMission ? "1px solid rgba(255,151,55,.75)" : "0",
overflow: "hidden",
          boxSizing: "border-box",
          padding: "8px 12px",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundImage: "url('/bricks.png')",
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: mission.locked ? 0.2 : isActiveMission ? 0.08 : 0.12,
            pointerEvents: "none",
          }}
        />

        <div
          style={{
            position: "relative",
            zIndex: 2,
            color: "#fff",
            fontSize: 16,
            lineHeight: "21px",
            fontWeight: 600,
            textAlign: "left",
            textShadow: "2px 3px 0 #000",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            paddingRight: isClaimable || mission.completed ? 78 : 0,
          }}
        >
          {mission.title}
        </div>

        <div
          style={{
            position: "absolute",
            left: 12,
            right: 12,
            bottom: 8,
            height: 22,
            borderRadius: 999,
            background: "#050000",
            overflow: "hidden",
            zIndex: 2,
            boxShadow: "inset 0 2px 3px rgba(0,0,0,.8)",
          }}
        >
          {mission.locked ? (
            <div
              style={{
                width: 20,
                height: 20,
                borderRadius: "50%",
                background: "#4c4c4c",
                position: "absolute",
                left: 2,
                top: 1,
              }}
            />
          ) : (
            <div
              style={{
                width: `${percent}%`,
                height: "100%",
                borderRadius: 999,
                background:
                  "linear-gradient(90deg, #c73b2d 0%, #e6642d 52%, #f6a13b 100%)",
              }}
            />
          )}

          <div
            style={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              fontSize: 12,
              lineHeight: "22px",
              fontWeight: 600,
              textShadow: "1px 1px 0 #000",
              whiteSpace: "nowrap",
              pointerEvents: "none",
              zIndex: 5,
            }}
          >
            {mission.locked ? `OPENS IN ${lockedText}` : `${progress}/${target}`}
          </div>
        </div>

        {isClaimable ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              claimMission(mission);
            }}
            disabled={isClaiming}
            className="shadow1 game-button-small green-button"
            style={{
              position: "absolute",
              right: 12,
              top: 7,
              zIndex: 6,
              height: 26,
              minWidth: 64,
              padding: "0 8px",
              border: 0,
              borderRadius: 7,
              background: "#78c943",
              color: "#fff",
              fontSize: 13,
              lineHeight: "26px",
              fontWeight: 600,
              textAlign: "center",
              boxShadow: "0 3px 0 #2f6f16",
              cursor: isClaiming ? "default" : "pointer",
              opacity: isClaiming ? 0.75 : 1,
            }}
          >
            {isClaiming ? "..." : "CLAIM"}
          </button>
        ) : isReallyCompleted ? (
  <img
    src="/v2/check.png"
    alt=""
    style={{
      position: "absolute",
      right: 12,
      top: 8,
      zIndex: 6,
      width: 27,
      height: 27,
      objectFit: "contain",
      filter: "drop-shadow(0 2px 2px rgba(0,0,0,.75))",
    }}
  />
) : mission.locked ? (
  <div
    style={{
      position: "absolute",
      right: 7,
      top: 7,
      width: 27,
      height: 27,
      zIndex: 6,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      fontSize: 20,
      lineHeight: "27px",
      filter: "drop-shadow(0 2px 2px rgba(0,0,0,.75))",
    }}
  >
    <img src="/v2/lock.png" style={{height: 21}}/>
  </div>
) : null}
      </div>
    </motion.div>
  );
}
 

 return (
  <motion.div
    initial={{ opacity: 0, y: 6 }}
    animate={{ opacity: 1, y: 0 }}
    onClick={() => {
  if (isClaimable) claimMission(mission);
}}
    style={{
      height: compact ? 58 : 62,
      width: "100%",
      boxSizing: "border-box",
      position: "relative",
      overflow: "visible",
      cursor: isClaimable ? "pointer" : "default",
      opacity: mission.locked ? 0.72 : 1,
      backgroundImage: "url('/airdrop_rect.png')",
      backgroundSize: "100% 100%",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center center",
    }}
  >
    <div
      style={{
        position: "absolute",
        inset: 0,
        display: "grid",
        gridTemplateColumns: "32px 1fr",
        alignItems: "center",
        padding: "0px 10px",
        boxSizing: "border-box",
        gap: 8,
      }}
    >
      <div
        style={{
          width: 32,
          height: 32,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <img
          src={dailyIcon}
          alt=""
          style={{
            width: 32,
            height: 32,
            objectFit: "contain",
            filter: "drop-shadow(0 2px 2px rgba(0,0,0,.75))",
          }}
        />
      </div>

      <div
        style={{
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
        <div
          style={{
            color: "#fff",
            fontSize: 12,
            lineHeight: "18px",
            fontWeight: 600,
            textAlign: "left",
            textShadow: "2px 3px 0 #000",
            whiteSpace: "nowrap",
            overflow: "hidden",
            textOverflow: "ellipsis",
            maxWidth: "100%",
          }}
        >
          {mission.title}
        </div>

        <div
          style={{
            marginTop: 4,
            marginLeft: -5,
            display: "flex",
            alignItems: "center",
            gap: 1,
            color: "#ffd35c",
            fontSize: 16,
            lineHeight: "18px",
            fontWeight: 600,
            textShadow: "1px 2px 0 #000",
            whiteSpace: "nowrap",
          }}
        >
          {event?.currencyIcon ? (
            <img
              src={event.currencyIcon}
              alt=""
              style={{
                width: 18,
                height: 18,
                objectFit: "contain",
                filter: "drop-shadow(0 2px 2px rgba(0,0,0,.75))",
              }}
            />
          ) : null}

          <span>
            {formatCompactNumber(mission.reward)}
          </span>
        </div>
      </div>
    </div>

    {isReallyCompleted ? (
  <img
    src="/v2/check.png"
    alt=""
    style={{
      position: "absolute",
      right: -4,
      top: -4,
      width: 27,
      height: 27,
      objectFit: "contain",
      zIndex: 5,
      filter: "drop-shadow(0 2px 2px rgba(0,0,0,.75))",
    }}
  />
) : isClaimable ? (
  <button
    type="button"
    onClick={(e) => {
      e.stopPropagation();
      claimMission(mission);
    }}
    disabled={isClaiming}
    style={{
      position: "absolute",
      right: -6,
      top: -8,
      zIndex: 6,
      height: 27,
      minWidth: 64,
      padding: "0 8px",
      border: 0,
      borderRadius: 7,
      background: "#78c943",
      color: "#fff",
      fontSize: 12,
      lineHeight: "27px",
      fontWeight: 900,
      textAlign: "center",
      textShadow: "1px 2px 0 #000",
      boxShadow: "0 3px 0 #2f6f16",
      cursor: isClaiming ? "default" : "pointer",
      opacity: isClaiming ? 0.75 : 1,
    }}
  >
    {isClaiming ? "..." : "CLAIM"}
  </button>
) : null}
  </motion.div>
);
};

const getSeasonalName = (item) => {
  const skinKeys = Object.keys(SEASONAL_SKINS).sort(
    (a, b) => b.length - a.length
  );

  const normalize = (value) =>
    String(value || "")
      .trim()
      .toLowerCase()
      .replace(/\\/g, "/");

  const values = [];

  const collectStrings = (value, depth = 0) => {
    if (depth > 6 || value == null) return;

    if (
      typeof value === "string" ||
      typeof value === "number"
    ) {
      values.push(normalize(value));
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((entry) =>
        collectStrings(entry, depth + 1)
      );
      return;
    }

    if (typeof value === "object") {
      Object.values(value).forEach((entry) =>
        collectStrings(entry, depth + 1)
      );
    }
  };

  collectStrings(item);

  /*
   * Primeiro procura correspondência EXATA.
   *
   * Como as keys estão ordenadas da maior para a menor:
   * anubis2 vem antes de anubis
   * croc2 vem antes de croc
   */
  for (const key of skinKeys) {
    const wanted = normalize(key);

    if (values.some((value) => value === wanted)) {
      return key;
    }
  }

  /*
   * Depois procura a skin em paths/nomes.
   *
   * Exemplos:
   * /assets/seasonal/anubis2/body.png
   * Anubis2 Skin
   */
  for (const key of skinKeys) {
    const wanted = normalize(key);

    const found = values.some((value) => {
      const cleaned = value
        .replace(/[_-]+/g, " ")
        .replace(/[/.]+/g, " ");

      const words = cleaned
        .split(/\s+/)
        .filter(Boolean);

      return words.includes(wanted);
    });

    if (found) {
      return key;
    }
  }

  return "";
};
const demoSkinCard = {
  character: {
    body: {
      side: "good",
      rarity: "Common",
      variant: 1,
    },
    boots: {
      side: "good",
      rarity: "Common",
      variant: 1,
    },
    pants: {
      side: "good",
      rarity: "Common",
      variant: 1,
    },
    shirt: {
      side: "good",
      rarity: "Common",
      variant: 1,
    },
    neck: {
      side: "good",
      rarity: "Common",
      variant: 1,
    },
    face: {
      side: "good",
      rarity: "Common",
      variant: 1,
    },
    eyes: {
      side: "good",
      rarity: "Common",
      variant: 1,
    },
    mouth: {
      side: "good",
      rarity: "Common",
      variant: 1,
    },
    head: {
      side: "good",
      rarity: "Common",
      variant: 1,
    },
    weapons: {
      side: "good",
      rarity: "Common",
      variant: 1,
    },
  },
};

const renderEventSkinPreview = (item, size = 74) => {
  const seasonName = getSeasonalName(item);

  const order = [
    "body",
    "boots",
    "pants",
    "shirt",
    "neck",
    "face",
    "eyes",
    "mouth",
    "head",
    "weapons",
  ];

  const skin =
    SEASONAL_SKINS[String(seasonName || "").trim().toLowerCase()] || null;

  const layers = [];
  const usedSeasonalSrcs = new Set();

  for (const part of order) {
    const data = demoSkinCard.character[part];

    if (!data) continue;

    const defaultSrc =
      `/assets/${part}/${data.side}/${data.rarity}/${data.variant}_thumb.png`;

    const seasonalSrc = skin?.[part];

    if (seasonalSrc) {
      // Important:
      // Some skins use ONE full-body PNG for every character part.
      // Render that PNG only once.
      if (!usedSeasonalSrcs.has(seasonalSrc)) {
        usedSeasonalSrcs.add(seasonalSrc);

        layers.push({
          key: `seasonal-${seasonalSrc}`,
          src: seasonalSrc,
          fallback: null,
        });
      }

      continue;
    }

    // This seasonal skin does not replace this part,
    // so keep the normal character part underneath.
    layers.push({
      key: `default-${part}`,
      src: defaultSrc,
      fallback: null,
    });
  }

  return (
    <div
      style={{
        position: "relative",
        width: size,
        height: size,
      }}
    >
      {layers.map((layer) => (
        <img
          key={layer.key}
          src={layer.src}
          alt=""
          draggable={false}
          onError={(e) => {
            // Never retry, never loop, never replace another layer.
            e.currentTarget.onerror = null;
            e.currentTarget.style.visibility = "hidden";
          }}
          style={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "contain",
            pointerEvents: "none",
          }}
        />
      ))}
    </div>
  );
};

  const renderShopItemCard = (item, featured = false) => {
    const price = getPrice(item);

    if (/bundle/i.test(String(item?.name || ""))) {
}

    const itemType = normalizeItemType(
  item?.type ||
  item?._base?.type ||
  item?._raw?.type ||
  ""
);

const seasonalName = getSeasonalName(item);

const itemText = String(
  [
    item?.name,
    item?.type,
    item?.chestType,
    item?.chest_type,
    item?._base?.name,
    item?._base?.type,
    item?._base?.chestType,
    item?._base?.chest_type,
    item?._raw?.name,
    item?._raw?.type,
    item?._raw?.chestType,
    item?._raw?.chest_type,
  ]
    .filter(Boolean)
    .join(" ")
).toLowerCase();

const isChestItem =
  itemType === "chest" ||
  itemText.includes("chest");

const showFullSkin =
  featured &&
  !isChestItem &&
  !!seasonalName &&
  !!SEASONAL_SKINS[seasonalName];

    const stockCurrent = firstDefined(
      item?.stockCurrent,
      item?.stock_current,
      item?.owned,
      item?.limit_used,
      null
    );

    const stockMax = firstDefined(
      item?.stockMax,
      item?.stock_max,
      item?.limit,
      item?.buyLimit,
      null
    );



    const soldOut =
      item?.soldOut ||
      (stockMax !== null && Number(stockCurrent) >= Number(stockMax));

      const remainingStock =
  stockMax !== null
    ? Math.max(0, Number(stockMax) - Number(stockCurrent || 0))
    : null;

    return (
  <div
    key={item.id || item.name}
    onClick={() => openPurchase(item)}
   style={{
  width: "100%",
  height: featured ? 150 : 148,
  boxSizing: "border-box",
  position: "relative",
  overflow: "hidden",
  cursor: soldOut ? "not-allowed" : "pointer",
  opacity: soldOut ? 0.55 : 1,

  backgroundImage: 'url("/v2/item.png")',
  backgroundRepeat: "no-repeat",
  backgroundSize: "100% 100%",
  backgroundPosition: "center center",

  border: 0,
  borderRadius: 0,
  boxShadow: "none",
}}
      >
        {!!item?.info && (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setInfoItem(item);
            }}
            style={{
              position: "absolute",
              top: 5,
              right: 5,
              zIndex: 5,
              width: 22,
              height: 22,
              borderRadius: "50%",
              border: "1px solid rgba(255,255,255,.25)",
              background: "rgba(12,39,56,.9)",
              color: "#fff",
              fontSize: 14,
              lineHeight: "20px",
              cursor: "pointer",
            }}
          >
            i
          </button>
        )}
{stockMax !== null && (
  <div
    style={{
      position: "absolute",
      top: 8,
      left: 8,
      zIndex: 6,
      height: 20,
      padding: "0 7px",
      borderRadius: 7,
      background: soldOut
        ? "rgba(95, 8, 8, 0.92)"
        : "rgba(0,0,0,0.72)",
      color: soldOut ? "#ffb3b3" : "#fff",
      fontSize: 11,
      lineHeight: "20px",
      fontWeight: 700,
      textShadow: "1px 1px #000",
      whiteSpace: "nowrap",
    }}
  >
    {soldOut
      ? "SOLD OUT"
      : `${remainingStock} LEFT`}
  </div>
)}

        {showFullSkin ? (
  <div
    style={{
      position: "absolute",
      top: featured ? 18 : 20,
      left: "50%",
      transform: "translateX(-50%)",
      width: featured ? 76 : 64,
      height: featured ? 76 : 64,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    {renderEventSkinPreview(
      item,
      featured ? 76 : 64
    )}
  </div>
) : (
  <img
    src={getItemIcon(item)}
    alt={item?.name || "Item"}
    onError={(e) => {
      e.currentTarget.onerror = null;
      e.currentTarget.src = "/pack.png";
    }}
    style={{
      position: "absolute",
      top: featured ? 22 : 24,
      left: "50%",
      transform: "translateX(-50%)",
      width: featured ? 70 : 54,
      height: featured ? 70 : 54,
      objectFit: "contain",
      filter:
        "drop-shadow(0 3px 4px rgba(0,0,0,.8))",
    }}
  />
)}

        <div
  title={item?.name}
  style={{
    position: "absolute",
    top: featured ? 93 : 93,
    left: 4,
    width: "calc(100% - 8px)",
    height: 24,
    lineHeight: "24px",
    boxSizing: "border-box",
    backgroundColor: "#2e1b10",
    color: "#fff",
    fontWeight: 600,
    fontSize: featured ? 12 : 12,
    textAlign: "center",
    textShadow: "1px 2px #000",
    display: "block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  }}
>
  {item?.name || "Item"}
</div>

      <div style={{height: 24, bottom: 8,backgroundColor: "#6f432a",position: "absolute", left: 4, width: "calc(100% - 8px)"}}>
        <div
          className="amount"
          style={{
            backgroundColor: "transparent",
            position: "absolute",
            left: "50%",
            bottom: 0,
            transform: "translateX(-50%)",
            height: 24,
            fontSize: 12,
            lineHeight: "24px",
            backgroundImage: getPriceIcon(item) ? `url('${getPriceIcon(item)}')` : "none",
            backgroundSize: "10px",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "left center",
            paddingLeft: getPriceIcon(item) ? 14 : 0,
            color: "#fff",
            textShadow: "1px 1px #000",
            whiteSpace: "nowrap",
            fontWeight: 600
          }}
        >
          {formatCompactNumber(price)}
        </div></div>
      </div>
    );
  };

  if (loading && !event) {
  return (
    <div
      className="shop-container"
      style={{
        paddingTop: 0,
        paddingBottom: 0,
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 450,
          margin: "0 auto",
          minHeight: 430,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <LoadingSpinner text="Loading Event" size={64} />
      </div>
    </div>
  );
}

  if (!event) {
    return (
      <div
        className="shop-container"
        style={{
          minHeight: "100%",
          color: "#fff",
          textAlign: "center",
          padding: 20,
          boxSizing: "border-box",
        }}
      >
        <h2 style={{ color: "#ffbb01", textShadow: "2px 2px #000" }}>
          No Event Available
        </h2>

        <p style={{ fontSize: 18, textShadow: "1px 1px #000" }}>
          No active event was returned by the backend.
        </p>

        <button
          className="game-button green-button"
          type="button"
          onClick={loadCurrentEvent}
        >
          RETRY
        </button>
      </div>
    );
  }

  return (
  <div
    className="shop-container"
    style={{
      overflowX: "hidden",
      paddingBottom: 0,
      fontFamily: '"Fredoka", sans-serif',

      ...(HALLOWEEN_THEME ? {
        backgroundImage: "url('/theme_2_bg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center top",
        backgroundRepeat: "no-repeat",
        minHeight: "100%",
      } : {}),
    }}
  >
      <div
        style={{
          width: "100%",
          maxWidth: 390,
          margin: "0 auto",
          padding: "0 8px 14px",
          boxSizing: "border-box",
        }}
      >
        <div
          style={{
            marginLeft: -20,
            marginTop: -20,
            marginRight: -20,
            position: "relative",
            height: 130,
            overflow: "hidden",
            backgroundImage: "url('/halloweenbanner.png')",
            backgroundSize: "cover",
            backgroundRepeat: "repeat",
            backgroundPosition: "center bottom",
          }}
        >
          {event.bannerImage ? (
            <img
              src={event.bannerImage}
              alt=""
              onError={(e) => {
                e.currentTarget.style.display = "none";
              }}
              style={{
                position: "absolute",
                inset: 0,
                width: "100%",
                height: "100%",
                objectFit: "cover",
                opacity: 0.65,
              }}
            />
          ) : null}

          <div
            style={{
              position: "absolute",
              inset: 0,
              background: "linear-gradient(180deg, rgba(0,0,0,.25), rgba(0,0,0,.55))",
            }}
          />

          <div
          className="shadow2"
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 20,
              textAlign: "center",
              fontSize: 28,
              lineHeight: "34px",
              fontWeight: 600,
              color: "#fff"
            }}
          >
            {event.name}
          </div>

        <div
  style={{
    position: "absolute",
    left: "50%",
    bottom: 15,
    transform: "translateX(-50%)",
    zIndex: 5,
  }}
>
  <EventCountdownBox endsAt={currentEndsAt} />
</div>
        </div>

       <div
  style={{
    display: "grid",
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
    gap: 8,
    marginTop: 18,
    marginBottom: 18,
    alignItems: "stretch",
  }}
>
  <EventTabButton
    active={view === "home"}
    onClick={() => setView("home")}
  >
    {event?.tabs?.home || "HOME"}
  </EventTabButton>

  <EventTabButton
    active={view === "shop"}
    onClick={() => setView("shop")}
  >
    {event?.tabs?.shop || "EVENT SHOP"}
  </EventTabButton>

  <EventCurrencyHeaderButton
    active={view === "currency"}
    balance={eventBalance}
    currencyName={event.currencyName}
    currencyIcon={event.currencyIcon}
    onClick={() => setView("currency")}
  />
</div>
       {loading && (
  <div
    style={{
      minHeight: 90,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
    }}
  >
    <LoadingSpinner text="Updating Event" size={42} />
  </div>
)}

        {view === "home" && (
  <>
    <EventSection
      title="Daily Missions"
      bodyStyle={{
        marginBottom: 0,
      }}
    >
      {event?.missions?.daily?.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
            gap: 10,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {event.missions.daily.map((mission) => (
            <MissionCard
              key={mission.id}
              mission={mission}
              compact
              variant="daily"
            />
          ))}
        </div>
      ) : (
        <div
        className="shadow1"
          style={{
            color: "#fff",
            textAlign: "center",
            padding: 14,
            fontWeight: 600
          }}
        >
          No daily missions configured.
        </div>
      )}
    </EventSection>

    <EventSection
      title="Seasonal Missions"
      bodyStyle={{
        display: "flex",
        flexDirection: "column",
        gap: 8,
        marginBottom: 0,
      }}
    >
      {event?.missions?.seasonal?.length > 0 ? (
        event.missions.seasonal.map((mission) => (
          <MissionCard
            key={mission.id}
            mission={mission}
            variant="seasonal"
          />
        ))
      ) : (
        <div
          style={{
            color: "#fff",
            textAlign: "center",
            padding: 12,
          }}
        >
          No seasonal missions configured.
        </div>
      )}
    </EventSection>
  </>
)}

      {view === "currency" && (
  <EventSection
    title={`Buy ${event.currencyName || "Event Currency"}`}
    bodyStyle={{
      display: "grid",
      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
      gap: 10,
      paddingBottom: 5,
      boxSizing: "border-box",
      marginBottom: 0,
    }}
  >
    {event.currencyPacks.length === 0 ? (
      <div
        className="shadow1"
        style={{
          gridColumn: "1 / -1",
          color: "#fff",
          textAlign: "center",
          padding: 14,
          fontWeight: 600,
        }}
      >
        No event currency packs configured.
      </div>
    ) : (
      event.currencyPacks.map((sku, idx) => {
        const rawPrice = Number(sku.price || 0);
        const discountedPrice = Number(sku.discountStars || 0);

        const hasDiscount =
          discountedPrice > 0 &&
          discountedPrice < rawPrice;

        const finalPrice = hasDiscount
          ? discountedPrice
          : rawPrice;

          const discountPercent = hasDiscount
  ? Math.round(((rawPrice - discountedPrice) / rawPrice) * 100)
  : 0;

        return (
          <button
            key={sku.id || `event-currency-${idx}`}
            type="button"
            disabled={buyingCurrency}
            onClick={() => {
              setSelectedCurrencyPackIdx(idx);

              setCurrencyToBuy({
                idx,
                sku,
                title: event.currencyName || "Event Currency",
                amount: sku.amount,
                price: finalPrice,
                oldPrice: hasDiscount ? rawPrice : null,
                itemIcon: event.currencyIcon || "/ankh.png",
                priceIcon: "/tstar.svg",
                priceLabel: "Telegram Stars",
              });

              setConfirmCurrencyOpen(true);
            }}
            style={{
              width: "100%",
              boxSizing: "border-box",
              position: "relative",
              overflow: "hidden",
              cursor: buyingCurrency ? "default" : "pointer",
              borderRadius: 10,

              backgroundImage: 'url("/v2/item.png")',
              backgroundRepeat: "repeat",
              backgroundSize: "100% 100%",
              backgroundPosition: "center center",

              border: 0,
              padding: 0,
              opacity: buyingCurrency ? 0.65 : 1,
              appearance: "none",
              WebkitAppearance: "none",
            }}
          >
            {hasDiscount && (
              <div
                className="shadow1"
                style={{
                  backgroundImage: 'url("/v2/discount.png")',
                  backgroundRepeat: "repeat",
                  backgroundSize: "100%",
                  backgroundPosition: "center",

                  position: "absolute",
                  width: 42,
                  height: 24,
                  top: 10,
                  right: 3,
                  zIndex: 5,

                  color: "#fff",
                  fontSize: 12,
                  lineHeight: "22px",
                  paddingLeft: 7,
                  boxSizing: "border-box",
                  fontWeight: 600,
                }}
              >
                -{discountPercent}%
              </div>
            )}

            <div
              style={{
                float: "left",
                position: "relative",
                aspectRatio: 1.3,
                width: "100%",
              }}
            >
              <img
                src={event.currencyIcon || "/ankh.png"}
                alt={event.currencyName || "Event Currency"}
                draggable={false}
                onError={(e) => {
                  e.currentTarget.onerror = null;
                  e.currentTarget.src = "/ankh.png";
                }}
                style={{
                  position: "absolute",
                  left: "calc(50% - 28px)",
                  top: "calc(50% - 18px)",
                  width: 56,
                  height: 56,
                  objectFit: "contain",
                  filter: "drop-shadow(0 3px 4px rgba(0,0,0,.75))",
                }}
              />
            </div>

            <div
              className="shadow1"
              title={`${formatCompactNumber(sku.amount)} ${
                event.currencyName || "Event Currency"
              }`}
              style={{
                float: "left",
                width: "calc(100% - 6px)",
                margin: 3,
                marginBottom: 0,
                paddingLeft: 5,
                paddingRight: 5,
                boxSizing: "border-box",

                color: "#fff",
                fontSize: 12,
                fontWeight: 600,
                textAlign: "center",
                lineHeight: "24px",

                whiteSpace: "nowrap",
                overflow: "hidden",
                textOverflow: "ellipsis",

                background: "#341a0d",
              }}
            >
              {formatCompactNumber(sku.amount)}
            </div>

            <div
              style={{
                float: "left",
                width: "calc(100% - 6px)",
                boxSizing: "border-box",
                margin: 3,
                marginTop: 0,
                marginBottom: 6,

                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 5,

                color: "#fff",
                fontSize: 16,
                fontWeight: 600,
                background: "#7e4123",
                lineHeight: "24px",
              }}
            >
              <img
                src="/tstar.svg"
                alt=""
                draggable={false}
                style={{
                  width: 21,
                  height: 21,
                  objectFit: "contain",
                }}
              />

              <span className="shadow1">
                {formatCompactNumber(finalPrice)}
              </span>
            </div>
          </button>
        );
      })
    )}
  </EventSection>
)}

       {view === "shop" && (
  <>
    <EventSection
      title="Event Special Items"
      bodyStyle={{
        marginBottom: 0,
      }}
    >
      {event?.shop?.special?.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 8,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {event.shop.special.map((item) =>
            renderShopItemCard(item, true)
          )}
        </div>
      ) : (
        <div
          style={{
            color: "#fff",
            textAlign: "center",
            padding: 12,
          }}
        >
          No special items configured.
        </div>
      )}
    </EventSection>

    <EventSection
      title="Event Items"
      bodyStyle={{
        marginBottom: 0,
      }}
    >
      {event?.shop?.items?.length > 0 ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gap: 8,
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          {event.shop.items.map((item) =>
            renderShopItemCard(item)
          )}
        </div>
      ) : (
        <div
          style={{
            color: "#fff",
            textAlign: "center",
            padding: 12,
          }}
        >
          No event items configured.
        </div>
      )}
    </EventSection>
  </>
)}
      </div>

     {/* Confirm Purchase Modal */}
{showConfirm && selectedItem && (
  <div className="modal_box">

  <div className="modal_title">Confirm?<button className="modal_close"/></div>
    <motion.div
      className="modal_body"   initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
    >
    <div className="modal_inside">
      {getSeasonalName(selectedItem) &&
SEASONAL_SKINS[getSeasonalName(selectedItem)] ? (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      marginBottom: 10,
    }}
  >
    {renderEventSkinPreview(selectedItem, 160)}
  </div>
) : (
  <img
    src={getItemIcon(selectedItem)}
    alt={selectedItem.name}
    onError={(e) => {
      e.currentTarget.onerror = null;
      e.currentTarget.src = "/pack.png";
    }}
    style={{
      height: 80,
      maxWidth: 120,
      objectFit: "contain",
      marginBottom: 10,
      filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.7))",
    }}
  />
)}

      <div className="shadow2" style={{ fontWeight: 600, fontSize: 28}}>
        Confirm Purchase?
      </div>

      <p
      className="shadow1"
        style={{
          
          fontSize: 21,
          fontWeight: 600,
          marginBottom: 12,
        }}
      >
        Buy{" "}
        <strong style={{ color: "#09BDFF", textTransform: "capitalize" }}>
          {selectedItem.name}
        </strong>
        {" "}
        for{" "}
        <span style={{ color: "#ffbb01", fontWeight: "bold" }}>
          {formatCompactNumber(getPrice(selectedItem) * quantity)}
        </span>
        ?
      </p>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 8,
          marginBottom: 8,
        }}
      >
        {getPriceIcon(selectedItem) ? (
          <img
            src={getPriceIcon(selectedItem)}
            alt="currency"
            style={{ width: 32, height: 32, objectFit: "contain" }}
          />
        ) : null}

        <span
        className="shadow1"
          style={{
            fontWeight: 600,
            fontSize: 28,
          }}
        >
          {formatCompactNumber(getPrice(selectedItem) * quantity)}
        </span>
      </div>

      <div className="info">
      <div style={{ color: "#ddd", fontSize: 18, textShadow: "1px 1px #000" }}>
        Quantity
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: 12,
          margin: "0px 0",
        }}
      >
        <button
          type="button"
          onClick={() => setQuantity((q) => clampQuantity(q - 1))}
          disabled={buying}
          style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            fontSize: 26,
            fontWeight: "bold",
          }}
        >
          -
        </button>

        <div
          style={{
            minWidth: 90,
            textAlign: "center",
            color: "#ffbb01",
            fontSize: 26,
            fontWeight: "bold",
            textShadow: "1px 1px #000",
          }}
        >
          {quantity}
        </div>

        <button
          type="button"
          onClick={() => setQuantity((q) => clampQuantity(q + 1))}
          disabled={buying}
          style={{
            width: 42,
            height: 42,
            borderRadius: 10,
            fontSize: 26,
            fontWeight: "bold",
          }}
        >
          +
        </button>
        </div>
      </div>

</div>
      <div className="modal_footer">
        <button
        className="game-button green-button" style={{marginRight: 10}}
          type="button"
          onClick={purchaseSelectedItem}
          disabled={buying}
        >
          {buying ? "Buying..." : "Confirm"}
        </button>

        <button
        className="game-button red-button"
          type="button"
          onClick={() => setShowConfirm(false)}
          disabled={buying}
        >
          CANCEL
        </button>
      </div>
    </motion.div>
  </div>
)}

{/* Item Info Modal */}
{infoItem && (
  <div className="modal-backdrop">
    <motion.div
      className="modal fancy-success-modal"
      initial={{ scale: 0.8, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
    >
      <h2 style={{ color: "#ffbb01", marginBottom: 10 }}>
        {infoItem.name}
      </h2>

      <p
        style={{
          textShadow: "1px 1px #000",
          fontSize: 20,
          color: "#ddd",
          lineHeight: "26px",
          marginBottom: 16,
        }}
      >
        {infoItem.info}
      </p>

      <div className="modal-footer">
        <button
          type="button"
          onClick={() => setInfoItem(null)}
        >
          OK
        </button>
      </div>
    </motion.div>
  </div>
)}

{/* Success Modal */}
{showSuccess && (
  <div className="modal_box">

  <div className="modal_title">Success<button onClick={() => {
    setShowSuccess(false);
    setSuccessReward(null);
  }} className="modal_close"/></div>

    <motion.div
      className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
    >

    <div className="modal_inside">
      <img
        src="/finger.png"
        alt="Success"
        style={{
          height: 80,
          marginBottom: 10,
          filter: "drop-shadow(0 3px 6px rgba(0,0,0,0.7))",
        }}
      />

      <div className="shadow2" style={{ fontWeight: 600, fontSize: 28, marginBottom: 10}}>
        Success!
      </div>

      <div className="shadow1"
        style={{
          fontWeight: 600,
          fontSize: 21,
          marginBottom: 10
        }}
      >
        {successMessage}
      </div>

      {successReward && Number(successReward.amount || 0) > 0 && (
  <div
  className="info"
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: 8,
    }}
  >
    {successReward.icon ? (
      <img
        src={successReward.icon}
        alt=""
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
        style={{
          width: 34,
          height: 34,
          objectFit: "contain",
          filter: "drop-shadow(0 2px 2px rgba(0,0,0,.75))",
        }}
      />
    ) : null}

    <div
      style={{
        color: "#ffbb01",
        fontSize: 26,
        lineHeight: "30px",
        fontWeight: 900,
        textShadow: "1px 2px 0 #000",
        whiteSpace: "nowrap",
      }}
    >
      +{formatCompactNumber(successReward.amount)}
    </div>

    {successReward.label ? (
      <div
        style={{
          color: "#fff",
          fontSize: 18,
          lineHeight: "22px",
          fontWeight: 300,
          textShadow: "1px 2px 0 #000",
          whiteSpace: "nowrap",
          maxWidth: 180,
          overflow: "hidden",
          textOverflow: "ellipsis",
          fontWeight: 600
        }}
      >
        {successReward.label}
      </div>
    ) : null}
  </div>
)}
</div>
      <div className="modal_footer">
        <button className="game-button red-button"
  type="button"
  onClick={() => {
    setShowSuccess(false);
    setSuccessReward(null);
  }}
>
  CLOSE
</button>
      </div>
    </motion.div>
  </div>
)}


{confirmCurrencyOpen &&
  currencyToBuy &&
  !showError &&
  !showSuccess && (
    <div
      className="modal_box"
    >
      <div className="modal_title">
        Confirm?

        <button
          type="button"
          className="modal_close"
          disabled={buyingCurrency}
          onClick={() => {
            if (buyingCurrency) return;

            setConfirmCurrencyOpen(false);
            setCurrencyToBuy(null);
            setSelectedCurrencyPackIdx(null);
          }}
        />
      </div>

      <motion.div className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}>
        <div className="modal_inside">
          <img
            src={currencyToBuy.itemIcon}
            alt={currencyToBuy.title}
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = "/ankh.png";
            }}
            style={{
              height: 80,
              objectFit: "contain",
            }}
          />

          <div
            className="shadow2"
            style={{
              fontSize: 28,
              fontWeight: 600,
              marginBottom: 10,
              marginTop: 10,
            }}
          >
            Confirm Purchase?
          </div>

          <p
            className="info shadow1"
            style={{
              fontSize: 21,
              color: "#ddd",
              lineHeight: "28px",
            }}
          >
            Buy{" "}
            <strong
              style={{
                color: "#09BDFF",
                textTransform: "capitalize",
              }}
            >
              {formatCompactNumber(currencyToBuy.amount)}{" "}
              {currencyToBuy.title}
            </strong>
            <br />
            for{" "}
            <span
              style={{
                color: "#ffbb01",
                fontWeight: "bold",
              }}
            >
              {currencyToBuy.oldPrice && (
                <span
                  style={{
                    textDecoration: "line-through",
                    opacity: 0.65,
                    marginRight: 6,
                  }}
                >
                  {formatCompactNumber(currencyToBuy.oldPrice)}
                </span>
              )}

              {formatCompactNumber(currencyToBuy.price)}{" "}
              {currencyToBuy.priceLabel}
            </span>
            ?
          </p>
        </div>

        <div className="modal_footer">
          <button
            type="button"
            className="game-button green-button"
            disabled={buyingCurrency}
            style={{ marginRight: 10 }}
            onClick={async () => {
              await purchaseEventCurrencyStars();

              setConfirmCurrencyOpen(false);
              setCurrencyToBuy(null);
            }}
          >
            {buyingCurrency ? "PROCESSING..." : "Confirm"}
          </button>

          <button
            type="button"
            className="game-button red-button"
            disabled={buyingCurrency}
            onClick={() => {
              setConfirmCurrencyOpen(false);
              setCurrencyToBuy(null);
              setSelectedCurrencyPackIdx(null);
            }}
          >
            CANCEL
          </button>
        </div>
      </motion.div>
    </div>
  )}

{/* Error Modal */}
{showError && (
  <div className="modal_box">
  <div className="modal_title">OOPS<button onClick={() => setShowError(false)} className="modal_close"/></div>
    <motion.div
      className="modal_body"  initial={{ scale: 0.8, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.92, opacity: 0 }}
    >
    <div className="modal_inside">
      <div className="shadow2" style={{fontWeight: 600, fontSize: 28, marginBottom: 10}}>
        Something Wrong!
      </div>

      <div
      className="info"
        style={{
          fontSize: 21,
          fontWeight: 600
        }}
      >
        {errorMessage || "Something went wrong"}
      </div>

      </div>

      <div className="modal_footer">
        <button className="game-button red-button"
          type="button"
          onClick={() => setShowError(false)}
        >
          CLOSE
        </button>
      </div>
    </motion.div>
  </div>
)}
    </div>
  );
}

export default EventsPage;